import {
    Box,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    CheckboxMenuTrigger,
    CheckboxMenuList,
    CheckboxMenuSearch,
    MenuRow,
    checkboxMenuPopoverContentSx,
} from '../ui/primitives/CheckboxMenu';

/** Above this many options the panel gets a search box. */
const SEARCH_THRESHOLD = 6;

export interface SearchableSingleSelectOption {
    value: string;
    label: string;
}

interface SearchableSingleSelectProps {
    options: SearchableSingleSelectOption[];
    value: string;
    onChange: (value: string) => void;
    width?: string;
    /** Shown when the value matches no option. */
    placeholder?: string;
    isDisabled?: boolean;
}

const SearchableSingleSelect = ({
    options,
    value,
    onChange,
    width = '280px',
    placeholder = 'Select...',
    isDisabled = false,
}: SearchableSingleSelectProps) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [search, setSearch] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);
    const [popoverWidth, setPopoverWidth] = useState<number | string>(width);

    const selected = options.find((option) => option.value === value);
    const selectedLabel = selected?.label ?? placeholder;
    const showSearch = options.length > SEARCH_THRESHOLD;

    const visibleOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => option.label.toLowerCase().includes(query));
    }, [options, search]);

    useEffect(() => {
        if (isPopoverOpen && triggerRef.current) {
            setPopoverWidth(Math.max(triggerRef.current.offsetWidth, 260));
        }
    }, [isPopoverOpen]);

    useEffect(() => {
        if (isDisabled && isPopoverOpen) setIsPopoverOpen(false);
    }, [isDisabled, isPopoverOpen]);

    const handleSelect = (nextValue: string) => {
        onChange(nextValue);
        setIsPopoverOpen(false);
    };

    return (
        <Popover
            placement="bottom-end"
            isOpen={isPopoverOpen}
            onOpen={() => {
                if (isDisabled) return;
                setSearch('');
                setIsPopoverOpen(true);
            }}
            onClose={() => setIsPopoverOpen(false)}
            gutter={8}
        >
            <PopoverTrigger>
                <Box w={width} ref={triggerRef} opacity={isDisabled ? 0.5 : 1} pointerEvents={isDisabled ? 'none' : undefined}>
                    <CheckboxMenuTrigger
                        isOpen={isPopoverOpen}
                        tokens={value ? [selectedLabel] : []}
                        emptyLabel={placeholder}
                    />
                </Box>
            </PopoverTrigger>
            <PopoverContent
                w={typeof popoverWidth === 'number' ? `${popoverWidth}px` : popoverWidth}
                sx={checkboxMenuPopoverContentSx}
            >
                <PopoverBody p={0}>
                    {showSearch && (
                        <CheckboxMenuSearch
                            value={search}
                            onChange={setSearch}
                            placeholder="Search"
                            autoFocus
                        />
                    )}
                    <CheckboxMenuList>
                        {visibleOptions.length === 0 && (
                            <Text fontSize="13px" color="gray.500" px="10px" py={2}>
                                No matches
                            </Text>
                        )}
                        {visibleOptions.map((option) => (
                            <MenuRow
                                key={option.value}
                                kind="single"
                                isSelected={option.value === value}
                                // Option labels here are sentences, not names,
                                // so they wrap rather than truncate.
                                allowWrap
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </MenuRow>
                        ))}
                    </CheckboxMenuList>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
};

export default SearchableSingleSelect;
